// run-last-outcome-file.ts — 06k C3: `runtime/last-run.json`, kết quả máy của lần
// `run` gần nhất (chỉ ghi khi `--json` hoặc `--detach` ⇒ đường mặc định không đổi
// byte, I1). CHỈ tiến trình đã giành machine lock ghi (B3): `running` qua callback
// `onMachineLockAcquired`, `exited` sau khi lock đã nhả, compare-and-write theo
// `run_id` — run khác của cùng mission đã giành lock sau ⇒ no-op. Liveness luôn
// đọc từ lock (C10); file này chỉ mang kết quả. Ghi tmp + rename (durable).
import { randomBytes } from "node:crypto";
import { readFile } from "node:fs/promises";
import { join } from "node:path";
import type { z } from "zod";
import { writeFileDurable } from "./durable-file-writer.js";
import { LastRunFileSchema } from "./schemas/status-schema.js";

export const LAST_RUN_FILE_NAME = "last-run.json";

export { LastRunFileSchema };

export type LastRunFile = z.infer<typeof LastRunFileSchema>;

export function lastRunPath(controlRoot: string): string {
  return join(controlRoot, LAST_RUN_FILE_NAME);
}

/** Nonce 128 bit — cha `--detach` sinh và truyền cho con qua `--run-id`. */
export function newRunId(): string {
  return randomBytes(16).toString("hex");
}

/** Hỏng / vắng / sai schema ⇒ `undefined` (không bao giờ ném). */
export async function readLastRun(controlRoot: string): Promise<LastRunFile | undefined> {
  try {
    const parsed = LastRunFileSchema.safeParse(
      JSON.parse(await readFile(lastRunPath(controlRoot), "utf8")),
    );
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

async function writeLastRun(controlRoot: string, file: LastRunFile): Promise<void> {
  await writeFileDurable(
    lastRunPath(controlRoot),
    Buffer.from(`${JSON.stringify(file)}\n`, "utf8"),
  );
}

export async function writeLastRunRunning(
  controlRoot: string,
  run: {
    runId: string;
    pid: number;
    detached: boolean;
    logPath?: string;
    startedAt?: string;
    processStartedAt?: string;
  },
): Promise<void> {
  await writeLastRun(controlRoot, {
    schema_version: 1,
    run_id: run.runId,
    state: "running",
    pid: run.pid,
    started_at: run.startedAt ?? new Date().toISOString(),
    detached: run.detached,
    ...(run.logPath === undefined ? {} : { log_path: run.logPath }),
    ...(run.processStartedAt === undefined ? {} : { process_started_at: run.processStartedAt }),
  });
}

/** CAS theo `run_id`: `false` (không ghi) khi file vắng hoặc thuộc run khác. */
export async function writeLastRunExited(
  controlRoot: string,
  runId: string,
  result: object | undefined,
): Promise<boolean> {
  const current = await readLastRun(controlRoot);
  if (current === undefined || current.run_id !== runId) return false;
  const { result: _previous, ...rest } = current;
  await writeLastRun(controlRoot, {
    ...rest,
    state: "exited",
    ...(result === undefined ? {} : { result: { ...result } }),
  });
  return true;
}
